import React from 'react';
import { observer } from 'mobx-react-lite';
import CheckboxTree from 'react-checkbox-tree';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, MinusIcon } from 'lucide-react';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';

import { Input } from 'src/domains/common/components/input/input';
import { Button } from 'src/domains/common/components/button/button';
import { Badge } from 'src/domains/common/components/badge/badge';
import { NodesType } from 'src/domains/common/components/multi-option-select/multi-option-select';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { cn } from 'src/domains/common/components/utils';

function getAllNodeValues(nodes: NodesType[]): string[] {
    let values: string[] = [];

    for (const node of nodes) {
        values.push(node.value);
        if (node.children !== undefined && node.children.length > 0) {
            values = [...values, ...getAllNodeValues(node.children)];
        }
    }

    return values;
}

interface MultiSelectListType {
    filteredNodes: NodesType[];
    checked: string[];
    visibleCheckedWithLabels: { value: string; label: string }[];
    visibleChecked: { display: string[]; remaining: number };
    expanded: string[];
    showAllBadges: boolean;
    keywordInputState: FormInputState<string, unknown>;
    remaining: number;
    remainingLabel: string;
    noCascade?: boolean;
    disabled?: boolean;
    description?: string;
    errors?: string[];
    toggleShowAllBadges: () => void;
    handleExpand: (expanded: string[]) => void;
    handleCheck: (checked: string[]) => void;
    handleRemoveChecked: (value: string) => void;
    setKeyword: (value: string) => void;
    clearChecked?: () => void;
}

export const MultiSelectList = observer(
    ({
        keywordInputState,
        filteredNodes,
        checked,
        showAllBadges,
        expanded,
        visibleCheckedWithLabels,
        remaining,
        remainingLabel,
        noCascade,
        disabled,
        errors,
        description,
        toggleShowAllBadges,
        clearChecked,
        handleCheck,
        handleExpand,
        setKeyword,
        handleRemoveChecked,
    }: MultiSelectListType) => {
        /**
         * Maintains checked state during filtering by merging visible selections with previously
         * checked but currently filtered-out nodes. Fixes react-checkbox-tree's limitation where
         * selections would otherwise be lost when interacting with filtered results.
         */
        const handleFullCheck = (newChecked: string[]): void => {
            const visibleNodeValues = new Set(getAllNodeValues(filteredNodes));
            const invisibleChecked = checked.filter((value) => !visibleNodeValues.has(value));

            const combinedChecked = [...invisibleChecked, ...newChecked];

            handleCheck(combinedChecked);
        };
        const error = errors?.length === 0 || errors === undefined ? null : errors[0] ?? null;

        const showClearButton = clearChecked !== undefined;

        return (
            <div
                className={cn(
                    'flex flex-col gap-4 bg-muted p-2 rounded-md',
                    error === null ? null : 'border-destructive border border-solid',
                    disabled === true ? 'cursor-not-allowed opacity-50 pointer-events-none' : ''
                )}
            >
                <div className='flex gap-2'>
                    <Input
                        formInputState={keywordInputState}
                        placeholder='Search'
                        onChange={(e) => {
                            setKeyword(e.target.value);
                        }}
                        errors={[]}
                    />
                    {showClearButton && (
                        <Button
                            variant='outline'
                            onClick={clearChecked}
                            className='self-end'
                        >
                            Clear All
                        </Button>
                    )}
                </div>

                <div className='flex flex-wrap gap-2 empty:hidden'>
                    {visibleCheckedWithLabels.map(({ value, label }) => (
                        <Badge
                            key={value}
                            variant='outline'
                            icon='close'
                            onClick={() => handleRemoveChecked(value)}
                        >
                            {label}
                        </Badge>
                    ))}
                    {remaining > 0 && showAllBadges === false && (
                        <Badge
                            variant='outline'
                            onClick={toggleShowAllBadges}
                        >
                            +{remaining} {remainingLabel}
                        </Badge>
                    )}
                </div>

                <div className='flex flex-col gap-4 p-2 rounded-sm bg-background overflow-y-auto custom-scrollbar h-96'>
                    <CheckboxTree
                        nodes={filteredNodes}
                        checked={checked}
                        expanded={expanded}
                        onCheck={(newChecked) => handleFullCheck(newChecked)}
                        onExpand={(expanded) => handleExpand(expanded)}
                        expandOnClick
                        showNodeIcon={false}
                        icons={{
                            check: <CheckIcon className='block rounded size-full text-primary-foreground bg-primary' />,
                            expandClose: <ChevronDownIcon className='size-full text-foreground' />,
                            expandOpen: <ChevronUpIcon className='size-full text-foreground' />,
                            halfCheck: (
                                <MinusIcon className='block rounded size-full bg-primary text-primary-foreground' />
                            ),
                        }}
                        noCascade={noCascade}
                        disabled={disabled}
                    />
                </div>
                {description !== undefined && error === null && (
                    <span className='block mt-2 text-sm text-muted-foreground'>{description}</span>
                )}
                {error === null ? null : <span className='text-destructive text-sm block mt-2'>{error}</span>}
            </div>
        );
    }
);
